import { useCallback, useMemo } from 'react';
import { optimizerGroupIntentKey } from '../../utils/optimizerGenericGroupContract';
import { ATTRIBUTE_RULE_DEFAULT_VALUE, factorRuleKey } from './optimizerRuleRegistry';

const byIntentKey = (left, right) => (
    optimizerGroupIntentKey(left).localeCompare(optimizerGroupIntentKey(right))
);

// Every scope edits the same editor state held by useOptimizer, so the
// Portfolio, Factors, Attributes and Active Limits views never hold copies.
// Switching a rule off keeps its limit; only an explicit remove deletes it.
export function useOptimizerRuleActions({
    constraints,
    setConstraints,
    setConstraintToggles,
    groupConstraints = [],
    setGroupConstraints,
    handleAddConstraint,
    handleRemoveConstraint,
    groupRecordLimit = 0,
    onError = null,
    onGmvBudgetEdited = null,
}) {
    const setDesign = useCallback((design) => {
        setConstraints((previous) => ({ ...previous, design }));
    }, [setConstraints]);
    const setPortfolioRuleValue = useCallback((key, value) => {
        // A typed GMV budget stops following the checked names.
        if (key === 'gmv') onGmvBudgetEdited?.();
        setConstraints((previous) => ({
            ...previous,
            static_constraints: {
                ...previous.static_constraints,
                [key]: value,
            },
        }));
    }, [onGmvBudgetEdited, setConstraints]);

    const setPortfolioRuleEnabled = useCallback((key, enabled) => {
        setConstraintToggles((previous) => ({
            ...previous,
            static_constraints: {
                ...previous.static_constraints,
                [key]: enabled,
            },
        }));
        if (enabled && constraints?.static_constraints?.[key] == null) {
            setPortfolioRuleValue(key, 0);
        }
    }, [constraints, setConstraintToggles, setPortfolioRuleValue]);

    const updateFactorRecord = useCallback((factor, patch) => {
        const key = factorRuleKey(factor);
        setConstraints((previous) => {
            const record = previous.dynamic_constraints?.[key];
            if (!record) return previous;
            return {
                ...previous,
                dynamic_constraints: {
                    ...previous.dynamic_constraints,
                    [key]: { ...record, ...patch },
                },
            };
        });
    }, [setConstraints]);

    const setFactorRuleEnabled = useCallback((factor, enabled) => {
        if (enabled) {
            handleAddConstraint(factor, 'Factor');
            return;
        }
        updateFactorRecord(factor, { enabled: false });
    }, [handleAddConstraint, updateFactorRecord]);

    const setFactorRuleValue = useCallback((factor, value) => {
        updateFactorRecord(factor, { value });
    }, [updateFactorRecord]);

    const removeFactorRule = useCallback((factor) => {
        handleRemoveConstraint(factorRuleKey(factor));
    }, [handleRemoveConstraint]);

    const setGroupRuleEnabled = useCallback(({
        attributeDefinitionId,
        group,
        metricName,
    }, enabled) => {
        const candidate = {
            attribute_definition_id: attributeDefinitionId,
            group_kind: group.group_kind,
            group_value_key: group.group_value_key ?? null,
            name: metricName,
        };
        const identity = optimizerGroupIntentKey(candidate);
        const exists = groupConstraints.some((record) => (
            optimizerGroupIntentKey(record) === identity
        ));
        if (exists) {
            setGroupConstraints((current) => current.map((record) => (
                optimizerGroupIntentKey(record) === identity
                    ? { ...record, enabled }
                    : record
            )));
            return;
        }
        if (!enabled) return;
        if (groupConstraints.length >= groupRecordLimit) {
            onError?.(`A maximum of ${groupRecordLimit} attribute constraints is allowed.`);
            return;
        }
        setGroupConstraints((current) => [
            ...current,
            { ...candidate, enabled: true, value: ATTRIBUTE_RULE_DEFAULT_VALUE },
        ].sort(byIntentKey));
        onError?.(null);
    }, [groupConstraints, groupRecordLimit, onError, setGroupConstraints]);

    const setGroupRuleValue = useCallback((identity, value) => {
        setGroupConstraints((current) => current.map((record) => (
            optimizerGroupIntentKey(record) === identity
                ? { ...record, value }
                : record
        )));
    }, [setGroupConstraints]);

    const setGroupRuleEnabledByIdentity = useCallback((identity, enabled) => {
        setGroupConstraints((current) => current.map((record) => (
            optimizerGroupIntentKey(record) === identity
                ? { ...record, enabled }
                : record
        )));
    }, [setGroupConstraints]);

    const removeGroupRule = useCallback((identity) => {
        setGroupConstraints((current) => current.filter((record) => (
            optimizerGroupIntentKey(record) !== identity
        )));
        onError?.(null);
    }, [onError, setGroupConstraints]);

    return useMemo(() => ({
        setDesign,
        setPortfolioRuleValue,
        setPortfolioRuleEnabled,
        setFactorRuleEnabled,
        setFactorRuleValue,
        removeFactorRule,
        setGroupRuleEnabled,
        setGroupRuleEnabledByIdentity,
        setGroupRuleValue,
        removeGroupRule,
    }), [
        setDesign,
        removeFactorRule,
        removeGroupRule,
        setFactorRuleEnabled,
        setFactorRuleValue,
        setGroupRuleEnabled,
        setGroupRuleEnabledByIdentity,
        setGroupRuleValue,
        setPortfolioRuleEnabled,
        setPortfolioRuleValue,
    ]);
}
